'use client'

import { ServicesList as ServicesListType , Media } from '@/payload-types'
import React, { useState } from 'react'
import { motion, AnimatePresence } from "motion/react" // Ensure you're using `framer-motion`
import { ChevronDown, ExternalLink } from "lucide-react"
import { CMSLink } from "@/components/Link"
import { TypedLocale } from 'payload'

export const ServiceListBlock: React.FC<ServicesListType & {
  locale: TypedLocale
}> = ({
  services,
  title,
  blockName,
  locale,
}) => {
    // Change state to store an array of expanded service IDs
    const [expandedServiceIds, setExpandedServiceIds] = useState<string[]>([])

    const toggleExpand = (serviceId: string) => {
      setExpandedServiceIds(currentIds =>
        currentIds.includes(serviceId)
          ? currentIds.filter(id => id !== serviceId) // Remove the ID if it's already expanded
          : [...currentIds, serviceId] // Add the ID if it's not expanded
      )
    }

    const renderSubServices = (service: NonNullable<ServicesListType['services']>[number]) => {
      if (!service.subs?.length) return null;

      const isExpanded = service.id ? expandedServiceIds.includes(service.id) : false;

      return (
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {service.subs.map((sub) => (
                  <div
                    key={sub.id}
                    className="bg-card p-4 rounded-lg shadow-sm"
                  >
                    <h4 className="text-lg font-semibold mb-2">{sub.name}</h4>
                    {sub.description && <p className="text-gray-600 mb-2">{sub.description}</p>}
                    {sub.hasLink && sub.links?.[0] && (
                      <CMSLink
                        {...sub.links[0].link} className='text-accent hover:text-accent-1 inline-flex items-center'
                      />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )
    }

    if (!services?.length) return null;

    return (
      <section id={blockName || 'servicesList'}  data-theme="light" className="container bg-background min-h-fit mx-auto  my-4 rounded-2xl drop-shadow-2xl px-4 py-16">
        <div className="container mx-auto px-4 md:py-8 lg:*:px-12">
          <h2 className="text-4xl font-extrabold text-center mb-12 text-accent">
            {title}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => {
              const isExpanded = service.id ? expandedServiceIds.includes(service.id) : false;

              return (
                <motion.div
                  key={service.id}
                  className={`relative bg-card shadow-lg rounded-xl p-6 ${isExpanded ? "lg:col-span-2" : ""
                    }`}
                  initial={false}
                  animate={{
                    scale: isExpanded ? 1.02 : 1,
                    transition: { duration: 0.3, ease: "easeInOut" },
                  }}
                >
                  <div className="flex flex-col items-center text-center">
                    {service.icon && (
                      <div className="mb-4">
                        <img
                          src={typeof service.icon === 'number' ? '' : (service.icon as Media).url!}
                          alt={service.name}
                          className="w-auto h-16"
                        />
                      </div>
                    )}
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{service.name}</h3>
                      {service.description && <p className="mb-4">{service.description}</p>}
                      {service.hasLink && service.links?.[0] && (
                        <CMSLink
                          {...service.links[0].link}
                          className="text-accent hover:text-accent-1 inline-flex items-center mt-4"
                        />
                      )}
                    </div>
                  </div>

                  {service.subs && service.subs.length > 0 && (
                    <button
                      onClick={() => service.id && toggleExpand(service.id)}
                      className="absolute end-6 top-6 text-accent hover:text-accent-1 focus:outline-none"
                    >
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown size={24} />
                      </motion.div>
                    </button>
                  )}

                  {renderSubServices(service)}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    )
  }